/**
 * SettingsModal: settings dialog for the CI/CD product.
 *
 * Scoped to this product: there is no AI model, no SMTP/alerting, no
 * Confluence connection, no ServiceNow and no performance options. What
 * remains is the profile (display name, email, password) and branding
 * (title, logo, tab icon), branding is admin-only.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import MuiDialog from "@mui/material/Dialog";
import { useAppTheme } from "../theme/ThemeContext";
import { api } from "../utils/helpers";
import i18n, { SPRACHEN, setzeSprache } from "../i18n";

// The tabs carry the catalog key, not the rendered text: the constants live
// outside the component, so a rendered string would otherwise freeze the
// language at first load.
const TABS = [
  { id: "profile", labelKey: "bereich.profile", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
  { id: "sprache", labelKey: "bereich.sprache", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg> },
];

const ADMIN_TABS = [
  { id: "branding", labelKey: "bereich.branding", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="13.5" cy="6.5" r="2.5"/><path d="M17.08 9.42a10 10 0 01-14.14 0"/><path d="M2 12a10 10 0 0020 0"/></svg> },
];

// iio-styled input field
const SetInp = ({ label, value, onChange, type = "text", ph = "" }) => (
  <div className="iio-set-field">
    <label className="iio-set-lbl">{label}</label>
    <input className="iio-set-input" type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={ph}/>
  </div>
);

const SettingsModal = ({ onClose, onUpdate, user, onBrandingUpdate }) => {
  const { t } = useTranslation("einstellungen");
  useAppTheme();
  const [tab, setTab] = useState("profile");
  // Read from i18n instead of mirrored into state: the switch takes effect
  // immediately, and a second piece of state could drift out of sync.
  const aktuelleSprache = (i18n.language || "de").slice(0, 2);
  const [settings, setSettings] = useState(null);

  // Profile
  const [dn, setDn] = useState("");
  const [em, setEm] = useState("");
  // Change password (kept in the same tab so profile and password stay together)
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");

  // Branding
  const [brTitle, setBrTitle] = useState("");
  const [brSub, setBrSub] = useState("");
  const [logoPreview, setLogoPreview] = useState("");
  const [favPreview, setFavPreview] = useState("");
  const [favSource, setFavSource] = useState("");

  const [saving, setSaving] = useState(false);
  // The message carries its own error flag explicitly. Deriving it from the
  // German wording (matching on "Fehler...") would never work in English.
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api("/api/auth/settings").then(s => {
      setSettings(s);
      setDn(s.displayName || "");
      setEm(s.email || "");
    });
    if (user?.role === "admin") {
      fetch("/api/branding").then(r => r.json()).then(b => {
        setBrTitle(b.title || "");
        setBrSub(b.subtitle || "");
        setLogoPreview(b.logoUrl || "");
        setFavPreview(b.faviconUrl || "");
        setFavSource(b.faviconSource || "");
      });
    }
  }, [user]);

  // Error message with the server's reason: the reason stays in the server's language.
  const fehlerMeldung = (grund) => setMsg({ text: t("meldung.fehlerMit", { grund }), err: true });

  // Save profile (display name, email)
  const saveProfile = async () => {
    setSaving(true); setMsg(null);
    try {
      await api("/api/auth/settings", { method: "PUT", body: JSON.stringify({ displayName: dn, email: em }) });
      setMsg({ text: t("meldung.profilGespeichert"), err: false });
      if (dn) onUpdate({ displayName: dn });
    } catch (e) { fehlerMeldung(e.message); }
    setSaving(false);
  };

  // Change password
  const savePassword = async () => {
    if (!pwNew || pwNew.length < 6) { setMsg({ text: t("meldung.passwortZuKurz"), err: true }); return; }
    if (pwNew !== pwConfirm) { setMsg({ text: t("meldung.passwortUngleich"), err: true }); return; }
    setSaving(true); setMsg(null);
    try {
      await api("/api/auth/change-password", { method: "POST", body: JSON.stringify({ currentPassword: pwCurrent || undefined, newPassword: pwNew }) });
      setMsg({ text: t("meldung.passwortGeaendert"), err: false });
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
    } catch (e) { fehlerMeldung(e.message); }
    setSaving(false);
  };

  // Save branding (title/subtitle)
  const saveBranding = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await api("/api/branding", { method: "PUT", body: JSON.stringify({ title: brTitle, subtitle: brSub }) });
      if (onBrandingUpdate) onBrandingUpdate(r);
      setMsg({ text: t("meldung.brandingGespeichert"), err: false });
    } catch (e) { fehlerMeldung(e.message); }
    setSaving(false);
  };
  const applyBranding = (r) => {
    setLogoPreview(r.logoUrl || "");
    setFavPreview(r.faviconUrl || "");
    setFavSource(r.faviconSource || "");
    if (onBrandingUpdate) onBrandingUpdate(r);
  };
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = "";
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const b64 = ev.target.result;
      try {
        const r = await api("/api/branding", { method: "PUT", body: JSON.stringify({ logoBase64: b64 }) });
        applyBranding(r);
        setMsg({ text: r.faviconSource === "logo"
          ? t("meldung.logoHochgeladenMitVorschlag")
          : t("meldung.logoHochgeladen"), err: false });
      } catch (ex) { fehlerMeldung(ex.message); }
    };
    reader.readAsDataURL(file);
  };
  const removeLogo = async () => {
    try {
      const r = await api("/api/branding", { method: "PUT", body: JSON.stringify({ logoBase64: "" }) });
      applyBranding(r); setMsg({ text: t("meldung.logoEntfernt"), err: false });
    } catch (e) { fehlerMeldung(e.message); }
  };
  const handleFaviconUpload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = "";
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const b64 = ev.target.result;
      try {
        const r = await api("/api/branding", { method: "PUT", body: JSON.stringify({ faviconBase64: b64 }) });
        applyBranding(r); setMsg({ text: t("meldung.faviconHochgeladen"), err: false });
      } catch (ex) { fehlerMeldung(ex.message); }
    };
    reader.readAsDataURL(file);
  };
  const removeFavicon = async () => {
    try {
      const r = await api("/api/branding", { method: "PUT", body: JSON.stringify({ faviconBase64: "" }) });
      applyBranding(r); setMsg({ text: t("meldung.faviconEntfernt"), err: false });
    } catch (e) { fehlerMeldung(e.message); }
  };
  const adoptLogoAsFavicon = async () => {
    try {
      const r = await api("/api/branding", { method: "PUT", body: JSON.stringify({ faviconFromLogo: true }) });
      applyBranding(r); setMsg({ text: t("meldung.logoAlsFavicon"), err: false });
    } catch (e) { fehlerMeldung(e.message); }
  };

  const isAdmin = user?.role === "admin";
  const SH = ({ title, desc }) => <div style={{ marginBottom: 16 }}><div className="iio-set-h">{title}</div>{desc && <div className="iio-set-sub">{desc}</div>}</div>;
  const SF = ({ onClick, label }) => <div className="iio-set-foot"><button className="iio-btn iio-btn--primary" onClick={onClick} disabled={saving} style={{ padding: "9px 22px" }}>{saving ? "..." : (label || t("gemeinsam:aktion.speichern"))}</button></div>;

  // Parameter deliberately not named t: that would shadow the translation
  // function and the bug would only show up at runtime.
  const renderTab = (reiter) => (
    <button key={reiter.id} className={`iio-set-tab${tab === reiter.id ? " on" : ""}`} onClick={() => { setTab(reiter.id); setMsg(null); }}>
      {reiter.icon}<span className="lbl">{t(reiter.labelKey)}</span>
    </button>
  );

  if (!settings) return null;

  return (
    <MuiDialog open={true} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: "85vh", overflow: "hidden", borderRadius: "var(--iio-r-xl)", background: "var(--iio-bg-1)" } }}>
      <div className="iio">
        <div className="iio-set">
          {/* Left side */}
          <div className="iio-set-side">
            <div className="iio-set-side-head">{t("titel")}</div>
            <nav className="iio-set-nav">
              {TABS.map(renderTab)}
              {isAdmin && <>
                <div className="iio-set-div"/>
                <div className="iio-set-admin-lbl">{t("adminGruppe")}</div>
                {ADMIN_TABS.map(renderTab)}
              </>}
            </nav>
            <div className="iio-set-side-foot">
              <button className="iio-set-close" onClick={onClose}>{t("gemeinsam:aktion.schliessen")}</button>
            </div>
          </div>

          {/* Right side */}
          <div className="iio-set-main">
            {msg && <div className={`iio-set-msg${msg.err ? " err" : ""}`}>{msg.text}</div>}

            {tab === "profile" && <div className="iio-set-pane">
              <SH title={t("profil.titel")} desc={t("profil.beschreibung")}/>
              <div className="iio-set-row2">
                <SetInp label={t("profil.anzeigename")} value={dn} onChange={setDn}/>
                <SetInp label={t("profil.email")} value={em} onChange={setEm}/>
              </div>
              <SF onClick={saveProfile} label={t("profil.speichern")}/>

              <div style={{ marginTop: 26, paddingTop: 18, borderTop: "1px solid var(--iio-border-subtle)" }}>
                <SH title={t("profil.passwortTitel")} desc={t("profil.passwortBeschreibung")}/>
                <SetInp label={t("profil.passwortAktuell")} type="password" value={pwCurrent} onChange={setPwCurrent} ph={t("profil.passwortAktuell")}/>
                <div className="iio-set-row2" style={{ marginTop: 10 }}>
                  <SetInp label={t("profil.passwortNeu")} type="password" value={pwNew} onChange={setPwNew} ph={t("profil.phPasswortNeu")}/>
                  <SetInp label={t("profil.passwortBestaetigen")} type="password" value={pwConfirm} onChange={setPwConfirm} ph={t("profil.phPasswortBestaetigen")}/>
                </div>
                <SF onClick={savePassword} label={t("profil.passwortSpeichern")}/>
              </div>
            </div>}

            {tab === "sprache" && <div className="iio-set-pane">
              <SH title={t("sprache.titel")} desc={t("sprache.beschreibung")}/>
              <div style={{ marginBottom: 14 }}>
                <label className="iio-set-lbl">{t("sprache.feld")}</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {SPRACHEN.map(sp => (
                    <button key={sp.code} className={`iio-set-seg${aktuelleSprache === sp.code ? " on" : ""}`} lang={sp.code}
                      aria-pressed={aktuelleSprache === sp.code} onClick={() => setzeSprache(sp.code)}>{sp.name}</button>))}
                </div>
              </div>
              <div style={{ fontSize: 11.5, color: "var(--iio-text-3)", lineHeight: 1.6 }}>{t("sprache.hinweis")}</div>
            </div>}

            {tab === "branding" && isAdmin && <div className="iio-set-pane">
              <SH title={t("branding.titel")} desc={t("branding.beschreibung")}/>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <SetInp label={t("branding.appTitel")} value={brTitle} onChange={setBrTitle} ph="abat CI/CD GUI"/>
                <SetInp label={t("branding.untertitel")} value={brSub} onChange={setBrSub} ph={t("branding.phUntertitel")}/>
                <div>
                  <label className="iio-set-lbl">{t("branding.logo")}</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {logoPreview
                      ? <img src={logoPreview} alt="" style={{ width: 44, height: 44, borderRadius: "var(--iio-r-md)", objectFit: "cover", border: "1px solid var(--iio-border)" }}/>
                      : <div style={{ width: 44, height: 44, borderRadius: "var(--iio-r-md)", background: "var(--iio-bg-2)", border: "1px solid var(--iio-border)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--iio-text-3)", fontSize: 11 }}>{t("branding.kein")}</div>}
                    <label className="iio-btn iio-btn--secondary" style={{ cursor: "pointer" }}>{t("branding.hochladen")}<input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: "none" }}/></label>
                    {logoPreview && <button className="iio-btn iio-btn--secondary" style={{ color: "var(--iio-critical)" }} onClick={removeLogo}>{t("branding.entfernen")}</button>}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--iio-text-3)", marginTop: 6 }}>{t("branding.logoHinweis")}</div>
                </div>
                <div>
                  <label className="iio-set-lbl">{t("branding.favicon")}</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {favPreview
                      ? <img src={favPreview} alt="" style={{ width: 44, height: 44, borderRadius: "var(--iio-r-md)", objectFit: "cover", border: "1px solid var(--iio-border)" }}/>
                      : <div style={{ width: 44, height: 44, borderRadius: "var(--iio-r-md)", background: "var(--iio-bg-2)", border: "1px solid var(--iio-border)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--iio-text-3)", fontSize: 11 }}>{t("branding.kein")}</div>}
                    <label className="iio-btn iio-btn--secondary" style={{ cursor: "pointer" }}>{t("branding.hochladen")}<input type="file" accept="image/*" onChange={handleFaviconUpload} style={{ display: "none" }}/></label>
                    {logoPreview && favSource !== "logo" && <button className="iio-btn iio-btn--secondary" onClick={adoptLogoAsFavicon}>{t("branding.logoUebernehmen")}</button>}
                    {favPreview && <button className="iio-btn iio-btn--secondary" style={{ color: "var(--iio-critical)" }} onClick={removeFavicon}>{t("branding.entfernen")}</button>}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--iio-text-3)", marginTop: 6 }}>
                    {favSource === "logo"
                      ? t("branding.faviconAusLogo")
                      : favSource === "custom"
                        ? t("branding.faviconEigen")
                        : t("branding.faviconHinweis")}
                  </div>
                </div>
              </div>
              <SF onClick={saveBranding} label={t("branding.speichern")}/>
            </div>}
          </div>
        </div>
      </div>
    </MuiDialog>
  );
};

export default SettingsModal;
